import { useBoundingClientRectMotion } from 'hooks/useBoundingClientRectMotion';
import { cn } from 'lib/utils';
import { motion, MotionValue, useSpring, useTransform } from 'motion/react';
import { FC, ReactNode, useRef } from 'react';

interface FeatureSmallCardProps {
  image: ReactNode;
  text: string;
  motionCursorX: MotionValue<number>;
  motionCursorY: MotionValue<number>;
}

const maxEffectDistance = 1000;
const epsilon = 1e-8;

export const FeatureSmallCard: FC<FeatureSmallCardProps> = ({
  image,
  text,
  motionCursorX,
  motionCursorY,
}) => {
  const blockRef = useRef<HTMLDivElement>(null);
  const boundingRectMotion = useBoundingClientRectMotion(blockRef);

  const itemXOffsetMotion = useTransform(() => {
    const { x, y, width, height } = boundingRectMotion.get();

    const cursorX = motionCursorX.get();
    const cursorY = motionCursorY.get();

    const xCenter = x + width / 2;
    const yCenter = y + height / 2;

    const dx = xCenter - cursorX;
    const dy = yCenter - cursorY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    const normalizedDx = dx / (distance + epsilon);

    const isInside = cursorX >= x && cursorX <= x + width && cursorY >= y && cursorY <= y + height;
    let strength = 0;

    if (isInside) {
      const normalizedDistanceX = (2 * Math.abs(dx)) / width;
      strength = normalizedDistanceX * 8;
    } else {
      const distanceOutsideX = Math.abs(dx) - width / 2;
      const falloff = Math.max(0, 1 - distanceOutsideX / maxEffectDistance);
      strength = falloff * 8;
    }

    return -normalizedDx * strength;
  });

  const itemYOffsetMotion = useTransform(() => {
    const { x, y, width, height } = boundingRectMotion.get();

    const cursorX = motionCursorX.get();
    const cursorY = motionCursorY.get();

    const xCenter = x + width / 2;
    const yCenter = y + height / 2;

    const dx = xCenter - cursorX;
    const dy = yCenter - cursorY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    const normalizedDy = dy / (distance + epsilon);

    const isInside = cursorX >= x && cursorX <= x + width && cursorY >= y && cursorY <= y + height;
    let strength = 0;

    if (isInside) {
      const normalizedDistanceY = (2 * Math.abs(dy)) / height;
      strength = normalizedDistanceY * 8;
    } else {
      const distanceOutsideY = Math.abs(dy) - height / 2;
      const falloff = Math.max(0, 1 - distanceOutsideY / maxEffectDistance);
      strength = falloff * 8;
    }

    return -normalizedDy * strength;
  });

  const springItemXOffsetMotion = useSpring(itemXOffsetMotion, { duration: 0.1 });
  const springItemYOffsetMotion = useSpring(itemYOffsetMotion, { duration: 0.1 });

  return (
    <motion.div
      style={{ x: springItemXOffsetMotion, y: springItemYOffsetMotion }}
      ref={blockRef}
      className="flex flex-col items-center gap-2 lg:min-w-20 z-1 py-2 px-3 origin-center"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.5, y: 50 }}
        whileInView={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'tween', ease: 'easeOut', duration: 0.6, delay: 0.2 }}
        className={cn('w-14')}
      >
        {image}
      </motion.div>
      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ type: 'tween', ease: 'easeOut', duration: 0.6, delay: 0.2 }}
        className="font-medium text-xs md:text-sm"
      >
        {text}
      </motion.p>
    </motion.div>
  );
};
